import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplates, createTemplate, updateTemplate, deleteTemplate } from '../store/slices/templateSlice';
import { fetchFooters, fetchDefaultFooter } from '../store/slices/footerSlice';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon, EyeIcon, PaperClipIcon, XMarkIcon, SwatchIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Templates.css';
import api from '../utils/api';
import TemplateDesigner from '../components/TemplateDesigner';
import ConfirmDialog from '../components/ConfirmDialog';

const Templates = () => {
  const dispatch = useDispatch();
  const { items, loading } = useSelector((state) => state.templates);
  const { user } = useSelector((state) => state.auth);
  const { items: footers, defaultFooter } = useSelector((state) => state.footers);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDesigner, setShowDesigner] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, template: null, campaigns: [] });
  const quillRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    footer: '',
    footerTemplateId: '',
    useFooterTemplate: false,
    attachments: [],
    designJson: null,
    allowedUserIds: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [individualSenders, setIndividualSenders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchTemplates());
    dispatch(fetchFooters());
    dispatch(fetchDefaultFooter());
    
    // Fetch users with individual email sending enabled (Super Admin only)
    if (user?.role === 'Super Admin') {
      fetchIndividualSenders();
    }
  }, [dispatch, user]);

  const fetchIndividualSenders = async () => {
    try {
      const response = await api.get('/users/individual-senders');
      setIndividualSenders(response.data);
    } catch (error) {
      console.error('Error fetching individual senders:', error);
    }
  };

  // Simple image upload handler
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/templates/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const imageUrl = `${window.location.origin}${response.data.file.path}`;

        // Simple approach: just add the image HTML to the current content
        const imageHtml = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
        const currentContent = formData.htmlContent || '';
        const newContent = currentContent + imageHtml;

        setFormData({ ...formData, htmlContent: newContent });

        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Image upload error:', error);
        toast.error('Failed to upload image');
      }
    };
  };

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchers: []
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      subject: template.subject || '',
      htmlContent: template.htmlContent || '',
      textContent: template.textContent || '',
      footer: template.footer || '',
      footerTemplateId: template.footerTemplateId || '',
      useFooterTemplate: !!template.footerTemplateId,
      attachments: template.attachments || [],
      designJson: template.designJson || null,
      allowedUserIds: template.allowedUserIds || null
    });
    setShowModal(true);
  };

  const handlePreview = async (template) => {
    // If template uses a footer template, fetch it
    if (template.footerTemplateId) {
      try {
        const response = await api.get(`/footers/${template.footerTemplateId}`);
        const footerTemplate = response.data;
        setPreviewTemplate({
          ...template,
          footerContent: footerTemplate.htmlContent || footerTemplate.content
        });
      } catch (error) {
        console.error('Error fetching footer template:', error);
        setPreviewTemplate(template);
      }
    } else {
      setPreviewTemplate(template);
    }
    setShowPreview(true);
  };

  const handleDesigner = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name || '',
        subject: template.subject || '',
        htmlContent: template.htmlContent || '',
        textContent: template.textContent || '',
        footer: template.footer || '',
        footerTemplateId: template.footerTemplateId || '',
        useFooterTemplate: !!template.footerTemplateId,
        attachments: template.attachments || [],
        designJson: template.designJson || null,
        allowedUserIds: template.allowedUserIds || null
      });
    } else {
      setEditingTemplate(null);
      resetForm();
    }
    setShowDesigner(true);
  };

  const handleDesignerContentChange = (content, design) => {
    setFormData(prev => ({ ...prev, htmlContent: content, designJson: design }));
  };

  const handleDesignerClose = () => {
    setShowDesigner(false);
    setEditingTemplate(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const response = await api.post('/templates/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({
        ...formData,
        attachments: [...formData.attachments, response.data.file]
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (editingTemplate) {
        await dispatch(updateTemplate({ id: editingTemplate.id, data: formData })).unwrap();
        toast.success('Template updated successfully');
      } else {
        await dispatch(createTemplate(formData)).unwrap();
        toast.success('Template created successfully');
      }
      setShowModal(false);
      resetForm();
    } catch (err) {
      toast.error(editingTemplate ? 'Failed to update template' : 'Failed to create template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (template) => {
    setDeleteDialog({ isOpen: true, template, campaigns: [] });
  };

  const confirmDelete = async () => {
    if (deleteDialog.template) {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await dispatch(deleteTemplate({ id: deleteDialog.template.id, force: false })).unwrap();
        toast.success('Template deleted successfully');
        setDeleteDialog({ isOpen: false, template: null, campaigns: [] });
      } catch (err) {
        console.error('Delete error:', err);

        // Handle specific error cases
        if (err.campaigns && err.campaigns.length > 0) {
          // Show campaigns that are using this template
          setDeleteDialog(prev => ({ ...prev, campaigns: err.campaigns }));
          toast.error(err.message || 'Cannot delete template - it is being used by campaigns');
        } else {
          toast.error(err.message || 'Failed to delete template');
          setDeleteDialog({ isOpen: false, template: null, campaigns: [] });
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const forceDelete = async () => {
    if (deleteDialog.template) {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await dispatch(deleteTemplate({ id: deleteDialog.template.id, force: true })).unwrap();
        toast.success(`Template and ${deleteDialog.campaigns.length} associated campaigns deleted successfully`);
        setDeleteDialog({ isOpen: false, template: null, campaigns: [] });
      } catch (err) {
        console.error('Force delete error:', err);
        toast.error(err.message || 'Failed to delete template and campaigns');
        setDeleteDialog({ isOpen: false, template: null, campaigns: [] });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ isOpen: false, template: null, campaigns: [] });
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      htmlContent: '',
      textContent: '',
      footer: '',
      footerTemplateId: '',
      useFooterTemplate: false,
      attachments: [],
      designJson: null,
      allowedUserIds: null
    });
  };

  const canCreate = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canEdit = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canDelete = ['superadmin', 'global_admin_hq'].includes(user?.role);

  // Filter templates based on search term
  const filteredItems = items.filter(template => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      template.name?.toLowerCase().includes(search) ||
      template.subject?.toLowerCase().includes(search)
    );
  });

  // Get user names for display
  const getUserNames = (allowedUserIds) => {
    if (!allowedUserIds || allowedUserIds.length === 0) return null;
    return allowedUserIds
      .map(id => individualSenders.find(u => u.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Email Templates</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 font-medium">Create and manage email templates</p>
        </div>
        {canCreate && (
          <button
            onClick={() => handleDesigner()}
            className="flex items-center px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
          >
            <SwatchIcon className="h-5 w-5 mr-2" />
            Visual Designer
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <input
          type="text"
          placeholder="Search templates by name or subject..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((template) => (
            <div key={template.id} className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-200 overflow-hidden border border-gray-200 hover:scale-105">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{template.name}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  <span className="font-semibold">Subject:</span> {template.subject}
                </p>
                
                {/* Assigned Users Info - Super Admin Only */}
                {user?.role === 'Super Admin' && template.allowedUserIds && template.allowedUserIds.length > 0 && (
                  <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700 font-semibold mb-1">Assigned to:</p>
                    <p className="text-xs text-blue-600">
                      {getUserNames(template.allowedUserIds) || `${template.allowedUserIds.length} user(s)`}
                    </p>
                  </div>
                )}
                
                {template.attachments && template.attachments.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Attachments:</p>
                    <div className="flex flex-wrap gap-1">
                      {template.attachments.map((file, idx) => (
                        <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs flex items-center font-medium">
                          <PaperClipIcon className="h-3 w-3 mr-1" />
                          {file.filename}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handlePreview(template)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 text-sm font-semibold shadow-md"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Preview
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => handleDesigner(template)}
                      className="flex items-center justify-center px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md"
                      title="Visual Designer"
                    >
                      <SwatchIcon className="h-4 w-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(template)}
                      className="flex items-center justify-center px-3 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all duration-200 shadow-md"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => { setShowModal(false); resetForm(); }}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[85vh] overflow-y-auto">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </h3>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Welcome Email"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Subject *</label>
                        <input
                          type="text"
                          required
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Welcome to TwilightX Mail!"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Content *</label>
                      <ReactQuill
                        key="simple-editor"
                        ref={quillRef}
                        theme="snow"
                        value={formData.htmlContent}
                        onChange={(value) => setFormData({ ...formData, htmlContent: value })}
                        modules={modules}
                        className="bg-white"
                        style={{ height: '300px', marginBottom: '50px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Footer Options</label>

                      {/* Footer Template Selection */}
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="useFooterTemplate"
                            name="footerOption"
                            checked={formData.useFooterTemplate}
                            onChange={(e) => setFormData({
                              ...formData,
                              useFooterTemplate: e.target.checked,
                              footer: e.target.checked ? '' : formData.footer
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="useFooterTemplate" className="ml-2 block text-sm text-gray-900">
                            Use Footer Template
                          </label>
                        </div>

                        {formData.useFooterTemplate && (
                          <div>
                            <select
                              value={formData.footerTemplateId}
                              onChange={(e) => setFormData({ ...formData, footerTemplateId: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select a footer template</option>
                              {defaultFooter && (
                                <option value={defaultFooter.id}>
                                  {defaultFooter.name} (Default)
                                </option>
                              )}
                              {footers.filter(f => f.id !== defaultFooter?.id && f.isActive).map(footer => (
                                <option key={footer.id} value={footer.id}>
                                  {footer.name}
                                </option>
                              ))}
                            </select>

                            {/* Footer Preview */}
                            {formData.footerTemplateId && (
                              <div className="mt-2 p-3 bg-gray-50 rounded border">
                                <div className="text-xs text-gray-600 mb-2">Footer Preview:</div>
                                <div
                                  className="text-sm"
                                  dangerouslySetInnerHTML={{
                                    __html: footers.find(f => f.id == formData.footerTemplateId)?.htmlContent ||
                                      defaultFooter?.htmlContent || ''
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="useCustomFooter"
                            name="footerOption"
                            checked={!formData.useFooterTemplate}
                            onChange={(e) => setFormData({
                              ...formData,
                              useFooterTemplate: !e.target.checked,
                              footerTemplateId: e.target.checked ? '' : formData.footerTemplateId
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="useCustomFooter" className="ml-2 block text-sm text-gray-900">
                            Custom Footer Text
                          </label>
                        </div>

                        {!formData.useFooterTemplate && (
                          <textarea
                            value={formData.footer}
                            onChange={(e) => setFormData({ ...formData, footer: e.target.value })}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., © 2024 Company Name. All rights reserved."
                          />
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Plain Text Content (Optional)</label>
                      <textarea
                        value={formData.textContent}
                        onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                        rows="4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Plain text version of the email (fallback for email clients that don't support HTML)"
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer">
                          <PaperClipIcon className="h-5 w-5 mr-2" />
                          {uploading ? 'Uploading...' : 'Upload File'}
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                        <span className="text-xs text-gray-500">Max 10MB (PDF, Images, Documents)</span>
                      </div>

                      {formData.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {formData.attachments.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <PaperClipIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{file.filename}</p>
                                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(idx)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* User Assignment - Super Admin Only */}
                    {user?.role === 'Super Admin' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assign to Individual Senders (Optional)
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Leave empty to make this template available to all users. Select specific users to restrict access.
                        </p>
                        
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="allUsers"
                              name="userAccess"
                              checked={formData.allowedUserIds === null}
                              onChange={() => setFormData({ ...formData, allowedUserIds: null })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="allUsers" className="ml-2 block text-sm text-gray-900">
                              Available to all users
                            </label>
                          </div>

                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="specificUsers"
                              name="userAccess"
                              checked={formData.allowedUserIds !== null}
                              onChange={() => setFormData({ ...formData, allowedUserIds: [] })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="specificUsers" className="ml-2 block text-sm text-gray-900">
                              Assign to specific individual senders
                            </label>
                          </div>

                          {formData.allowedUserIds !== null && (
                            <div className="ml-6 space-y-2">
                              {individualSenders.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">
                                  No users with individual email sending enabled
                                </p>
                              ) : (
                                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 space-y-2">
                                  {individualSenders.map(sender => (
                                    <div key={sender.id} className="flex items-center">
                                      <input
                                        type="checkbox"
                                        id={`user-${sender.id}`}
                                        checked={formData.allowedUserIds?.includes(sender.id) || false}
                                        onChange={(e) => {
                                          const currentIds = formData.allowedUserIds || [];
                                          if (e.target.checked) {
                                            setFormData({ 
                                              ...formData, 
                                              allowedUserIds: [...currentIds, sender.id] 
                                            });
                                          } else {
                                            setFormData({ 
                                              ...formData, 
                                              allowedUserIds: currentIds.filter(id => id !== sender.id) 
                                            });
                                          }
                                        }}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      />
                                      <label htmlFor={`user-${sender.id}`} className="ml-2 block text-sm text-gray-700">
                                        {sender.name} ({sender.sendingEmail || sender.email})
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                    {isSubmitting ? (editingTemplate ? 'Updating...' : 'Creating...') : (editingTemplate ? 'Update Template' : 'Create Template')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowPreview(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">Email Preview</h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  {/* Email Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <span className="font-medium mr-2">From:</span>
                      <span>noreply@twilightxmail.com</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <span className="font-medium mr-2">To:</span>
                      <span>recipient@example.com</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-sm text-gray-600 mr-2">Subject:</span>
                      <span className="text-lg font-semibold text-gray-900">{previewTemplate.subject}</span>
                    </div>
                  </div>

                  {/* Email Body */}
                  <div className="bg-white p-6 max-h-[50vh] overflow-y-auto">
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewTemplate.htmlContent }}
                    />

                    {/* Footer from Footer Template */}
                    {previewTemplate.footerContent && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{ __html: previewTemplate.footerContent }}
                        />
                      </div>
                    )}

                    {/* Footer from inline text */}
                    {!previewTemplate.footerContent && previewTemplate.footer && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="text-sm text-gray-600 whitespace-pre-wrap">
                          {previewTemplate.footer}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Attachments Info */}
                  {previewTemplate.attachments && previewTemplate.attachments.length > 0 && (
                    <div className="bg-blue-50 px-6 py-4 border-t border-blue-200">
                      <p className="text-sm text-blue-800 mb-2 font-medium">
                        Attachments ({previewTemplate.attachments.length}):
                      </p>
                      <div className="space-y-1">
                        {previewTemplate.attachments.map((file, idx) => (
                          <div key={idx} className="flex items-center text-sm text-blue-700">
                            <PaperClipIcon className="h-4 w-4 mr-2" />
                            <span>{file.filename}</span>
                            <span className="ml-2 text-xs text-blue-600">({(file.size / 1024).toFixed(2)} KB)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowPreview(false)}
                  className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visual Designer Modal */}
      {showDesigner && (
        <div className="fixed inset-0 z-50">
          <TemplateDesigner
            initialContent={formData.htmlContent}
            initialDesign={formData.designJson}
            onContentChange={handleDesignerContentChange}
            onClose={handleDesignerClose}
            templateData={formData}
            onTemplateDataChange={setFormData}
            onSave={async (html, design) => {
              if (!formData.name || !formData.subject) {
                toast.error('Please provide template name and subject');
                return;
              }
              try {
                const payload = { ...formData, htmlContent: html, designJson: design };
                if (editingTemplate) {
                  await dispatch(updateTemplate({ id: editingTemplate.id, data: payload })).unwrap();
                  toast.success('Template updated successfully');
                } else {
                  await dispatch(createTemplate(payload)).unwrap();
                  toast.success('Template created successfully');
                }
                setShowDesigner(false);
                resetForm();
              } catch (err) {
                toast.error(editingTemplate ? 'Failed to update template' : 'Failed to create template');
              }
            }}
          />



        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={deleteDialog.campaigns.length > 0 ? cancelDelete : confirmDelete}
        onSecondaryAction={deleteDialog.campaigns.length > 0 ? forceDelete : undefined}
        title="Delete Template"
        message={
          deleteDialog.campaigns.length > 0
            ? `Cannot delete "${deleteDialog.template?.name}" because it is being used by ${deleteDialog.campaigns.length} campaign(s): ${deleteDialog.campaigns.map(c => c.name).join(', ')}. You can either update those campaigns to use a different template, or delete the campaigns along with this template.`
            : `Are you sure you want to delete "${deleteDialog.template?.name}"? This action cannot be undone.`
        }
        confirmText={deleteDialog.campaigns.length > 0 ? "OK" : "Delete Template"}
        secondaryText={deleteDialog.campaigns.length > 0 ? "Delete Campaigns & Template" : undefined}
        cancelText={deleteDialog.campaigns.length > 0 ? "Cancel" : "Cancel"}
        type="danger"
      />
    </div>
  );
};

export default Templates;
